import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Divider,
  Row,
} from '@/components';
import { useLocaledAmount } from '@/providers';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import { AmountUnitLabelModifier, formatValueByUnit } from '@/utils';
import { ReviewTypeLabelModifier } from '@/utils/enum';
import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  ActionOfReflectionStatusLabelModifier,
  InspectionByExternalExpertReasonModifier,
  InspectionEvaluationRsModel,
  OpinionByAdministrativeReviewTeamLabelModifier,
  ProcessTypeLabelModifier,
} from '../../../_models/project';
import { ReflectedAmountOfSpecialAccountDiffView } from './ReflectedAmountOfSpecialAccountDiffView';

const PartialInspectionEvaluationRsModel =
  InspectionEvaluationRsModel.partial();

type Props = {
  sourceInspectionEvaluation: z.infer<
    typeof PartialInspectionEvaluationRsModel
  >; // left side
  targetInspectionEvaluation: z.infer<
    typeof PartialInspectionEvaluationRsModel
  >; // right side
};

export const InspectionEvaluationDiffView: FC<Props> = ({
  sourceInspectionEvaluation,
  targetInspectionEvaluation,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const targetSummariesAndResponses =
    targetInspectionEvaluation.summariesAndResponses ?? [];
  const sourceSummariesAndResponses =
    sourceInspectionEvaluation.summariesAndResponses ?? [];

  const targetIndicatedItemsAndResponses =
    targetInspectionEvaluation.otherIndicatedItemsAndResponses ?? [];
  const sourceIndicatedItemsAndResponses =
    sourceInspectionEvaluation.otherIndicatedItemsAndResponses ?? [];

  const InspectionAndImprovementByAdministrativeJurisdictionModel =
    InspectionEvaluationRsModel.shape
      .inspectionAndImprovementByAdministrativeJurisdiction;
  type InspectionAndImprovementByAdministrativeJurisdiction = z.infer<
    typeof InspectionAndImprovementByAdministrativeJurisdictionModel
  >;

  const inspectionAndImprovementByAdministrativeJurisdictionDiffViewConfig: Array<
    DiffViewConfigType<InspectionAndImprovementByAdministrativeJurisdiction>
  > = [
    {
      prop: 'inspectionResult',
      label: t('inspection-result'),
    },
    {
      prop: 'evaluation',
      label: t('evaluation'),
    },
    {
      prop: 'directionOfImprovement',
      label: t('direction-of-improvement'),
    },
  ];

  const InspectionByExternalExpertModel =
    InspectionEvaluationRsModel.shape.inspectionByExternalExpert;
  type InspectionByExternalExpert = z.infer<
    typeof InspectionByExternalExpertModel
  >;
  const inspectionByExternalExpertDiffViewConfig: Array<
    DiffViewConfigType<InspectionByExternalExpert>
  > = [
    {
      prop: 'reviewType',
      label: t('inspection-target'),
      converter: (_, data) =>
        data?.reviewType ? ReviewTypeLabelModifier[data.reviewType] : '',
    },
    {
      prop: 'lastImplementedFiscalYear',
      label: t('last-implemented-fiscal-year'),
    },
    {
      prop: 'reason',
      label: t('reason-of-target'),
      converter: (_, data) =>
        data?.reason
          ? InspectionByExternalExpertReasonModifier[data.reason]
          : '',
    },
    {
      prop: 'opinion',
      label: t('opinion'),
      isLongField: true,
    },
    {
      prop: 'resultOfPublishing',
      label: t('result-of-publishing'),
      isLongField: true,
    },
  ];

  const ProcessAndOpinionByAdministrativeReviewTeamModel =
    InspectionEvaluationRsModel.shape
      .processAndOpinionByAdministrativeReviewTeam;
  type ProcessAndOpinionByAdministrativeReviewTeam = z.infer<
    typeof ProcessAndOpinionByAdministrativeReviewTeamModel
  >;

  const processAndOpinionByAdministrativeReviewTeamDiffViewConfig: Array<
    DiffViewConfigType<ProcessAndOpinionByAdministrativeReviewTeam>
  > = [
    {
      prop: 'opinion',
      label: t('opinion'),
      converter: (_, data) =>
        data?.opinion
          ? OpinionByAdministrativeReviewTeamLabelModifier[data.opinion]
          : '',
    },
    {
      prop: 'context',
      label: t('details'),
      isLongField: true,
    },
  ];

  const ReflectionStatusBasedOnOpinionModel =
    InspectionEvaluationRsModel.shape.reflectionStatusBasedOnOpinion;
  type ReflectionStatusBasedOnOpinion = z.infer<
    typeof ReflectionStatusBasedOnOpinionModel
  >;

  const reflectionStatusBasedOnOpinionDiffViewConfig: Array<
    DiffViewConfigType<ReflectionStatusBasedOnOpinion>
  > = [
    {
      prop: 'action',
      label: t('improvement-and-reflection-status'),
      converter: (_, data) =>
        data?.action ? ActionOfReflectionStatusLabelModifier[data.action] : '',
    },
    {
      prop: 'reflectedAmountOfGeneralAccount',
      label: t('reflected-amount-of-general-account-with-unit', {
        unit: AmountUnitLabelModifier[unit],
      }),
      converter: (_, data) =>
        data?.reflectedAmountOfGeneralAccount
          ? formatValueByUnit(data.reflectedAmountOfGeneralAccount, {
              operation: 'round',
              unit,
            })
          : '',
      isMonetaryField: true,
    },
    {
      prop: 'specialAccount',
      label: t('reflected-amount-of-special-account'),
    },
    {
      prop: 'context',
      label: t('details'),
      isLongField: true,
    },
  ];

  const SummariesAndResponsesModel =
    InspectionEvaluationRsModel.shape.summariesAndResponses;
  const SummaryAndResponseModel = SummariesAndResponsesModel.element;
  type SummaryAndResponseType = z.infer<typeof SummaryAndResponseModel>;

  const summaryAndResponseDiffViewConfig: Array<
    DiffViewConfigType<SummaryAndResponseType>
  > = [
    {
      prop: 'process',
      label: t('summary-process'),
      converter: (value: SummaryAndResponseType['process']) =>
        value ? ProcessTypeLabelModifier[value] : '',
    },
    {
      prop: 'fiscalYear',
      label: t('summary-fiscal-year'),
    },
    {
      prop: 'content',
      label: t('summary-content'),
      isLongField: true,
    },
    {
      prop: 'response',
      label: t('response-status'),
      isLongField: true,
    },
  ];

  const SummaryAndResponseLength = Math.max(
    targetSummariesAndResponses.length,
    sourceSummariesAndResponses.length,
  );

  const IndicatedItemsAndResponsesModel =
    InspectionEvaluationRsModel.shape.otherIndicatedItemsAndResponses;
  const IndicatedItemAndResponseModel = IndicatedItemsAndResponsesModel.element;
  type IndicatedItemAndResponseType = z.infer<
    typeof IndicatedItemAndResponseModel
  >;
  const indicatedItemAndResponseDiffViewConfig: Array<
    DiffViewConfigType<IndicatedItemAndResponseType>
  > = [
    {
      prop: 'reason',
      label: t('indicated-reason'),
    },
    {
      prop: 'fiscalYear',
      label: t('indicated-fiscal-year'),
    },
    {
      prop: 'content',
      label: t('indicated-content'),
      isLongField: true,
    },
    {
      prop: 'response',
      label: t('response-status'),
      isLongField: true,
    },
  ];

  const IndicatedItemResponseLength = Math.max(
    targetIndicatedItemsAndResponses.length,
    sourceIndicatedItemsAndResponses.length,
  );

  return (
    <Card>
      <Row gutter={[8, 0]}>
        <DiffViewerTitleCols
          label={t('inspection-and-improvement-by-administrative-jurisdiction')}
        />
        {inspectionAndImprovementByAdministrativeJurisdictionDiffViewConfig.map(
          ({ prop, label }) => (
            <DiffViewerContentCols
              key={`inspection-and-improvement-by-administrative-jurisdiction-${prop}`}
              legend={label}
              prop={prop}
              isLongField
              showLegendAnytime={{ source: true, target: true }}
              source={
                sourceInspectionEvaluation.inspectionAndImprovementByAdministrativeJurisdiction
              }
              target={
                targetInspectionEvaluation.inspectionAndImprovementByAdministrativeJurisdiction
              }
            />
          ),
        )}

        <DiffViewerTitleCols label={t('inspection-by-external-expert')} />
        {inspectionByExternalExpertDiffViewConfig.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols
              key={`inspection-by-external-expert-${prop}`}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              showLegendAnytime={{ source: true, target: true }}
              source={sourceInspectionEvaluation.inspectionByExternalExpert}
              target={targetInspectionEvaluation.inspectionByExternalExpert}
            />
          ),
        )}

        <DiffViewerTitleCols
          label={t('process-and-opinion-by-administrative-review-team')}
        />
        {processAndOpinionByAdministrativeReviewTeamDiffViewConfig.map(
          ({ prop, label, converter, isLongField }) => (
            <DiffViewerContentCols
              key={`process-and-opinion-by-administrative-review-team-${prop}`}
              legend={label}
              prop={prop}
              textConverter={converter}
              isLongField={isLongField}
              showLegendAnytime={{ source: true, target: true }}
              source={
                sourceInspectionEvaluation.processAndOpinionByAdministrativeReviewTeam
              }
              target={
                targetInspectionEvaluation.processAndOpinionByAdministrativeReviewTeam
              }
            />
          ),
        )}

        <DiffViewerTitleCols label={t('reflection-status-based-on-opinion')} />
        {reflectionStatusBasedOnOpinionDiffViewConfig.map(
          ({ prop, label, converter, isLongField, isMonetaryField }) => {
            if (prop === 'specialAccount') {
              return (
                <ReflectedAmountOfSpecialAccountDiffView
                  key={`reflection-status-based-on-opinion-${prop}`}
                  sourceReflectedAmountOfSpecialAccounts={
                    sourceInspectionEvaluation.reflectionStatusBasedOnOpinion
                      ?.specialAccount ?? []
                  }
                  targetReflectedAmountOfSpecialAccounts={
                    targetInspectionEvaluation.reflectionStatusBasedOnOpinion
                      ?.specialAccount ?? []
                  }
                />
              );
            }
            return (
              <DiffViewerContentCols
                key={`reflection-status-based-on-opinion-${prop}`}
                legend={label}
                prop={prop}
                textConverter={converter}
                isLongField={isLongField}
                isMonetaryField={isMonetaryField}
                showLegendAnytime={{ source: true, target: true }}
                source={
                  sourceInspectionEvaluation.reflectionStatusBasedOnOpinion
                }
                target={
                  targetInspectionEvaluation.reflectionStatusBasedOnOpinion
                }
              />
            );
          },
        )}

        <DiffViewerTitleCols label={t('indicated-item-and-response')} />
        {[...Array(SummaryAndResponseLength)].map((_, index) => {
          const source = sourceSummariesAndResponses[index];
          const target = targetSummariesAndResponses[index];
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            <Fragment key={`summary-and-response-${index}`}>
              <Divider />
              <DiffViewerTitleCols
                index={index + 1}
                label={t('summary-of-publishing')}
              />
              {summaryAndResponseDiffViewConfig.map(
                ({ prop, label, converter, isLongField }) => (
                  <DiffViewerContentCols
                    key={`summary-and-response-${prop}`}
                    legend={label}
                    prop={prop}
                    textConverter={converter}
                    isLongField={isLongField}
                    showLegendAnytime={{ source: true, target: true }}
                    source={source}
                    target={target}
                  />
                ),
              )}
            </Fragment>
          );
        })}

        {[...Array(IndicatedItemResponseLength)].map((_, index) => {
          const source = sourceIndicatedItemsAndResponses[index];
          const target = targetIndicatedItemsAndResponses[index];
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            <Fragment key={`indicated-item-and-response-${index}`}>
              <Divider />
              <DiffViewerTitleCols
                index={index + 1}
                label={t('others-indicated-item')}
              />
              {indicatedItemAndResponseDiffViewConfig.map(
                ({ prop, label, isLongField }) => (
                  <DiffViewerContentCols
                    key={`indicated-item-and-response-${prop}`}
                    legend={label}
                    prop={prop}
                    isLongField={isLongField}
                    showLegendAnytime={{ source: true, target: true }}
                    source={source}
                    target={target}
                  />
                ),
              )}
            </Fragment>
          );
        })}
      </Row>
    </Card>
  );
};
